/*
React select component that searches and displays business sheets tied to a client master account.
*/

import React from "react";
import styled from "styled-components";
import classNames from "classnames";
import AsyncSelect from "react-select/async";
import Button from "../../buttons";
import DateDisplay from "../../DateDisplay";
import FormGroup from "../FormGroup.js";
import FormLabel from "../FormLabel.js";
import FormInputGroup from "../FormInputGroup.js";
import { components, MultiValue, SingleValue, OptionProps } from "react-select";
import { DefReactSelectOption } from "../../../types/DefReactSelectOption.js";
import { PagedResults } from "../../../types/PagedResults.js";
import { useLazySearchBizSheetsQuery } from "../../../services/recommendations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

export type BusinessSheetOptionProps = {
    id: string,
    status: string,
    reportName: string,
    reportStatus: string,
    createdDate: string
};

export type ClientBusinessSheetSearchSelectProps = {
    allowCreate?: boolean,
    className?: any,
    error?: Error | string | undefined,
    isClearable?: boolean,
    isDisabled?: boolean,
    isMulti?: boolean,
    label?: string,
    masterAccountId: string,
    onChange: (v: MultiValue<DefReactSelectOption<BusinessSheetOptionProps>> | SingleValue<DefReactSelectOption<BusinessSheetOptionProps>>) => void,
    value: string | string[] | null
};

const ClientBusinessSheetSelectOptionSubHeading = styled.div`
    font-size: 0.8em;
    color: ${(props: any) => props.isSelected === true ? props.theme.light : props.theme.muted};
`;

export const ClientBusinessSheetSelectOption = (props: OptionProps<DefReactSelectOption<BusinessSheetOptionProps>, boolean, any>) => {
    const { data: { data } } = props;

    if (!data) {
        return (
            <components.Option {...props}>
                <span>{props.label}</span>
            </components.Option>
        );
    }

    const { createdDate, reportName, reportStatus, status } = data;
    return (
        <components.Option {...props}>
            <span>{reportName}</span>
            <ClientBusinessSheetSelectOptionSubHeading className="d-flex font-monospace" isSelected={props.isSelected}>
                <div className="row flex-fill">
                    <div className="col-6">
                        <span>Status:</span> {status}
                    </div>
                    <div className="col-6">
                        <span>Report:</span> {reportStatus}
                    </div>
                </div>
                <div className="text-end">
                    <span>Created:</span> <DateDisplay>{createdDate}</DateDisplay>
                </div>
            </ClientBusinessSheetSelectOptionSubHeading>
        </components.Option>
    )
};

export const ClientBusinessSheetSearchSelect = React.forwardRef<any, ClientBusinessSheetSearchSelectProps>((
    { 
        allowCreate = false, 
        className, 
        isClearable = false, 
        isDisabled = false, 
        isMulti = false, 
        label = "Business Sheet", 
        masterAccountId, 
        onChange, 
        value 
    } : Readonly<ClientBusinessSheetSearchSelectProps>,
    ref
) => {
    const componentId = React.useId();
    const [additionalRecords, setAdditionalRecords] = React.useState<BusinessSheetOptionProps[]>([]);
    const [cacheKey, setCacheKey] = React.useState<number>(new Date().valueOf());
    const [load, { data }] = useLazySearchBizSheetsQuery();

    // called to determine what the default value supplied to the select should/would be based on the options
    const buildDefaultValue = () : DefReactSelectOption<BusinessSheetOptionProps> | DefReactSelectOption<BusinessSheetOptionProps>[] | null => {
        if (!value || !data) {
            return null;
        }
    
        const selectedValue = value;
        const { results } = data as PagedResults<BusinessSheetOptionProps>;

        const resultSets : DefReactSelectOption<BusinessSheetOptionProps>[] = [
            ...additionalRecords.map((el) => ({ label: el.reportName, value: el.id, data: el, isDisabled: false } as DefReactSelectOption<BusinessSheetOptionProps>)), 
            ...results.map((el) => ({ label: el.reportName, value: el.id, data: el, isDisabled: false } as DefReactSelectOption<BusinessSheetOptionProps>))
        ];

        if (resultSets.length === 0) {
            return null;
        }

        if (isMulti === true) {
            const selectedValues : string[] = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
            return resultSets.filter(({ value }) => selectedValues.includes(value));
        }

        const foundResult = resultSets.find(({ value }) => value === selectedValue) ?? null;
        return foundResult;
    };

    // called by the async select to get a list of business sheets
    const loadOptions = (input: string, callback: (options: DefReactSelectOption<BusinessSheetOptionProps>[]) => void) => {
        const newCacheKey = new Date().valueOf();
        load(
            { cacheKey: newCacheKey, masterAccountId, searchTerm: input, page: 1, limit: 50 }
        )
        .then(
            ({ data }: { data: PagedResults<BusinessSheetOptionProps> }) => {
                if (data && data.results.length > 0) {
                    const records: DefReactSelectOption<BusinessSheetOptionProps>[] = data.results.map(el => ({
                        label: el.reportName,
                        value: el.id,
                        data: el,
                        isDisabled: false,
                        isFixed: false
                    } as DefReactSelectOption<BusinessSheetOptionProps>));
                    callback(records);
                }
                else {
                    callback([]);
                }
            }
        )
        .finally(() => {
            setCacheKey(() => newCacheKey);
        });
    };

    // triggered when the create button is clicked
    const onCreateClicked = () => {
        if (!allowCreate) {
            return;
        }
        // TODO: get the modal to display
    };

    const displayValue = React.useMemo(() => buildDefaultValue(), [ data, isMulti, value ]);
    return (
        <FormGroup>
            <FormLabel htmlFor={componentId}>{label}</FormLabel>
            <FormInputGroup>
                <AsyncSelect 
                    id={componentId}
                    className={classNames('flex-fill', className)}
                    ref={ref}
                    cacheOptions={true}
                    defaultOptions={true}
                    loadOptions={loadOptions}
                    isClearable={isClearable}
                    isDisabled={isDisabled}
                    isMulti={isMulti}
                    value={displayValue}
                    onChange={(e) => onChange(e)}
                    components={{
                        Option: ClientBusinessSheetSelectOption
                    }}
                />
                {allowCreate && (
                    <Button variant="success" type="button" className="text-nowrap" disabled={isDisabled} onClick={onCreateClicked}>
                        <FontAwesomeIcon icon={faPlus} />
                    </Button>
                )}
            </FormInputGroup>
        </FormGroup>
    );
});